import {UI_COLORS} from '@/utils/ui-colors';

export function calculateDayOffset(momentLib, rangeStart, dateString) {
    if (!rangeStart || !dateString) {
        return 0;
    }
    const start = momentLib(rangeStart, 'YYYY-MM-DD').startOf('day');
    const current = momentLib(dateString, 'YYYY-MM-DD').startOf('day');
    return current.diff(start, 'days');
}

export function buildTimelineMonths(momentLib, rangeStart, rangeEnd, pixelsPerDay, dayOffsetFn, locale) {
    if (!rangeStart || !rangeEnd) {
        return [];
    }

    const start = momentLib(rangeStart, 'YYYY-MM-DD').startOf('month');
    const end = momentLib(rangeEnd, 'YYYY-MM-DD').endOf('month');
    const items = [];
    const cursor = start.clone();

    while (cursor.isSameOrBefore(end, 'month')) {
        const monthStart = cursor.clone().startOf('month');
        const monthEnd = cursor.clone().endOf('month');
        const visibleStart = momentLib.max(monthStart, momentLib(rangeStart, 'YYYY-MM-DD').startOf('day'));
        const visibleEnd = momentLib.min(monthEnd, momentLib(rangeEnd, 'YYYY-MM-DD').startOf('day'));
        const dayOffset = dayOffsetFn(visibleStart.format('YYYY-MM-DD'));
        const daySpan = visibleEnd.diff(visibleStart, 'days') + 1;

        items.push({
            key: cursor.format('YYYY-MM'),
            label: cursor.locale(locale).format('MMM YYYY'),
            left: dayOffset * pixelsPerDay,
            width: Math.max(1, daySpan * pixelsPerDay),
        });
        cursor.add(1, 'month');
    }

    return items;
}

export function buildTimelineWeeks(momentLib, rangeStart, rangeEnd, pixelsPerDay, dayOffsetFn) {
    if (!rangeStart || !rangeEnd) {
        return [];
    }

    const normalizedRangeStart = momentLib(rangeStart, 'YYYY-MM-DD').startOf('day');
    const normalizedRangeEnd = momentLib(rangeEnd, 'YYYY-MM-DD').startOf('day');
    const start = normalizedRangeStart.clone().startOf('isoWeek');
    const end = normalizedRangeEnd.clone().endOf('isoWeek');
    const items = [];
    const cursor = start.clone();

    while (cursor.isSameOrBefore(end, 'day')) {
        const weekStart = cursor.clone().startOf('isoWeek');
        const weekEnd = cursor.clone().endOf('isoWeek');
        const visibleStart = momentLib.max(weekStart, normalizedRangeStart);
        const visibleEnd = momentLib.min(weekEnd, normalizedRangeEnd);
        const daySpan = visibleEnd.diff(visibleStart, 'days') + 1;

        if (daySpan > 0) {
            const dayOffset = dayOffsetFn(visibleStart.format('YYYY-MM-DD'));
            items.push({
                key: cursor.format('YYYY-MM-DD'),
                week: cursor.isoWeek(),
                start_date: visibleStart.format('YYYY-MM-DD'),
                end_date: visibleEnd.format('YYYY-MM-DD'),
                left: dayOffset * pixelsPerDay,
                width: Math.max(1, daySpan * pixelsPerDay),
            });
        }
        cursor.add(1, 'week');
    }

    return items;
}

export function calculateTodayLeft(today, rangeStart, rangeEnd, totalDays, pixelsPerDay, dayOffsetFn) {
    if (!today || !rangeStart || !rangeEnd) {
        return null;
    }
    const offset = dayOffsetFn(today);
    if (offset < 0 || offset > totalDays) {
        return null;
    }
    return offset * pixelsPerDay;
}

export function buildSegmentMenuKey(row, segment) {
    const segmentId = segment && segment.id ? segment.id : 'segment';
    const startDate = segment && segment.display_start_date ? segment.display_start_date : '';
    const endDate = segment && segment.display_end_date ? segment.display_end_date : '';
    return [row.id, segmentId, startDate, endDate].join('-');
}

export function getTimelineSegments(row) {
    return row && Array.isArray(row.segments) ? row.segments : [];
}

export function getTimelineSegmentIndex(row, segment, segmentMenuKeyFn) {
    const segments = getTimelineSegments(row);
    return segments.findIndex((item) => segmentMenuKeyFn(row, item) === segmentMenuKeyFn(row, segment));
}

export function isRowMenuActive(row, activeSegmentMenuKey, segmentMenuKeyFn) {
    if (!row || !Array.isArray(row.segments) || !activeSegmentMenuKey) {
        return false;
    }
    return row.segments.some((segment) => segmentMenuKeyFn(row, segment) === activeSegmentMenuKey);
}

export function getChronologyStatusText(row, translateFn = null) {
    const planned = Number(row && row.planned_steps ? row.planned_steps : 0);
    const total = Number(row && row.total_steps ? row.total_steps : 0);
    const translate = typeof translateFn === 'function' ? translateFn : (key) => key;
    const applyReplacements = (text, replacements = {}) => {
        let result = String(text ?? '');
        Object.keys(replacements).forEach((replacementKey) => {
            const replacementValue = String(replacements[replacementKey]);
            result = result.split(':' + replacementKey).join(replacementValue);
            result = result.split('{' + replacementKey + '}').join(replacementValue);
        });
        return result;
    };

    if (total <= 0 || planned >= total) {
        return '';
    }
    if (planned <= 0) {
        return applyReplacements(translate('Chronology pending'));
    }
    return applyReplacements(
        translate('Chronology incomplete :planned/:total', { planned, total }),
        { planned, total }
    );
}

export function calculateRowHeight(row) {
    const segmentCount = row && Array.isArray(row.segments) ? row.segments.length : 0;
    const rowUnit = 36;
    const segmentTopOffset = 46;
    const segmentBarHeight = 30;
    const titleOnlyHeight = 44;

    if (segmentCount < 1) {
        return titleOnlyHeight;
    }

    const segmentsBottom = segmentTopOffset + ((segmentCount - 1) * rowUnit) + segmentBarHeight;
    const rowWithBottomPadding = segmentsBottom + 10;

    return Math.max(rowWithBottomPadding, titleOnlyHeight);
}

export function hasTimelineSegments(row) {
    return !!(row && Array.isArray(row.segments) && row.segments.length > 0);
}

export function buildSegmentStyle(segment, index, pixelsPerDay, dayOffsetFn, options = {}) {
    const leftDays = dayOffsetFn(segment.display_start_date);
    const endDays = dayOffsetFn(segment.display_end_date);
    const widthDays = Math.max(1, endDays - leftDays + 1);
    const rowUnit = 36;
    const barHeight = 30;
    const topOffset = 46;
    const hasActionMenu = !options.customerView;
    const actionPadding = hasActionMenu ? 22 : 8;
    const isBoundarySegment = !!(segment && (segment.is_mandatory_start || segment.is_mandatory_completion));
    const boundaryMinWidth = hasActionMenu ? 60 : 38;
    const boundaryRightPadding = hasActionMenu ? 26 : 12;
    const boundaryLeftPadding = 8;

    return {
        left: (leftDays * pixelsPerDay) + 'px',
        top: ((index * rowUnit) + topOffset) + 'px',
        width: Math.max(isBoundarySegment ? boundaryMinWidth : 2, widthDays * pixelsPerDay) + 'px',
        backgroundColor: segment.color || UI_COLORS.text.subtle,
        height: barHeight + 'px',
        borderTopLeftRadius: '3px',
        borderBottomLeftRadius: '3px',
        borderTopRightRadius: '3px',
        borderBottomRightRadius: '3px',
        paddingRight: (isBoundarySegment ? boundaryRightPadding : actionPadding) + 'px',
        paddingLeft: isBoundarySegment ? (boundaryLeftPadding + 'px') : '0',
        border: '0',
    };
}

export function resolveUserAvatarUrl(user) {
    if (!user) {
        return '';
    }
    if (user.avatar_url) {
        return user.avatar_url;
    }
    if (user.avatar && user.avatar !== 'gravatar') {
        return user.avatar;
    }
    if (user.gravatar) {
        return user.gravatar;
    }
    return '';
}
